import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { TenantProvider, ThemeProvider } from '@/contexts';
import { trackPageView } from '@/utils/analytics';

import { PublicLayout } from '@/components/layout/PublicLayout';
import { WebSocketManager } from '@/components/WebSocketManager';
import { PublicHomePage } from '@/pages/public';

const AuthLayout = lazy(() =>
  import('@/components/layout/AuthLayout').then((m) => ({ default: m.AuthLayout }))
);
const DashboardLayout = lazy(() =>
  import('@/components/layout/DashboardLayout').then((m) => ({ default: m.DashboardLayout }))
);

const SuperAdminGuard = lazy(() =>
  import('@/components/guards').then((m) => ({ default: m.SuperAdminGuard }))
);
const PermissionGuard = lazy(() =>
  import('@/components/guards').then((m) => ({ default: m.PermissionGuard }))
);
const SubscriptionGuard = lazy(() =>
  import('@/components/guards').then((m) => ({ default: m.SubscriptionGuard }))
);

const SignupPage = lazy(() =>
  import('@/pages/public').then((m) => ({ default: m.SignupPage }))
);
const SantinhoPage = lazy(() =>
  import('@/pages/public').then((m) => ({ default: m.SantinhoPage }))
);
const DemoPage = lazy(() =>
  import('@/pages/public').then((m) => ({ default: m.DemoPage }))
);
const LandingLancamento = lazy(() =>
  import('@/pages/landing').then((m) => ({ default: m.LandingLancamento }))
);
const LandingDeputados2026 = lazy(() =>
  import('@/pages/landing').then((m) => ({ default: m.LandingDeputados2026 }))
);
const TermsPage = lazy(() =>
  import('@/pages/legal').then((m) => ({ default: m.TermsPage }))
);
const PrivacyPage = lazy(() =>
  import('@/pages/legal').then((m) => ({ default: m.PrivacyPage }))
);

const LoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const CallbackPage = lazy(() =>
  import('@/pages/auth/CallbackPage').then((m) => ({ default: m.CallbackPage }))
);
const InvitePage = lazy(() =>
  import('@/pages/auth/InvitePage').then((m) => ({ default: m.InvitePage }))
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
);

const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const SmartRedirect = lazy(() =>
  import('@/components/SmartRedirect').then((m) => ({ default: m.SmartRedirect }))
);
const ContactsPage = lazy(() =>
  import('@/pages/contacts/ContactsPage').then((m) => ({ default: m.ContactsPage }))
);
const ContactFormPage = lazy(() =>
  import('@/pages/contacts/ContactFormPage').then((m) => ({ default: m.ContactFormPage }))
);
const ContactsMapPage = lazy(() =>
  import('@/pages/contacts/ContactsMapPage').then((m) => ({ default: m.ContactsMapPage }))
);
const OrganizationsPage = lazy(() =>
  import('@/pages/organizations/OrganizationsPage').then((m) => ({ default: m.OrganizationsPage }))
);
const OrganizationFormPage = lazy(() =>
  import('@/pages/organizations/OrganizationFormPage').then((m) => ({
    default: m.OrganizationFormPage,
  }))
);
const OrganizationsMapPage = lazy(() =>
  import('@/pages/organizations/OrganizationsMapPage').then((m) => ({
    default: m.OrganizationsMapPage,
  }))
);
const DemandsPage = lazy(() =>
  import('@/pages/demands/DemandsPage').then((m) => ({ default: m.DemandsPage }))
);
const SupportersPage = lazy(() =>
  import('@/pages/supporters/SupportersPage').then((m) => ({ default: m.SupportersPage }))
);
const SupporterFormPage = lazy(() =>
  import('@/pages/supporters/SupporterFormPage').then((m) => ({ default: m.SupporterFormPage }))
);
const MeMilitanciaPage = lazy(() =>
  import('@/pages/me/MeMilitanciaPage').then((m) => ({ default: m.MeMilitanciaPage }))
);
const TeamsPage = lazy(() =>
  import('@/pages/supporters/TeamsPage').then((m) => ({ default: m.TeamsPage }))
);
const TasksPage = lazy(() =>
  import('@/pages/supporters/TasksPage').then((m) => ({ default: m.TasksPage }))
);
const SupportersDashboardPage = lazy(() =>
  import('@/pages/supporters/SupportersDashboardPage').then((m) => ({
    default: m.SupportersDashboardPage,
  }))
);
const DemandFormPage = lazy(() =>
  import('@/pages/demands/DemandFormPage').then((m) => ({ default: m.DemandFormPage }))
);
const DemandsMapPage = lazy(() =>
  import('@/pages/demands/DemandsMapPage').then((m) => ({ default: m.DemandsMapPage }))
);
const ChatPage = lazy(() =>
  import('@/pages/chat/ChatPage').then((m) => ({ default: m.ChatPage }))
);
const CampaignsPage = lazy(() =>
  import('@/pages/campaigns/CampaignsPage').then((m) => ({ default: m.CampaignsPage }))
);
const CampaignFormPage = lazy(() =>
  import('@/pages/campaigns/CampaignFormPage').then((m) => ({ default: m.CampaignFormPage }))
);
const CampaignDetailPage = lazy(() =>
  import('@/pages/campaigns/CampaignDetailPage').then((m) => ({ default: m.CampaignDetailPage }))
);
const BirthdayCampaignsPage = lazy(() =>
  import('@/pages/campaigns/BirthdayCampaignsPage').then((m) => ({
    default: m.BirthdayCampaignsPage,
  }))
);
const FlowsPage = lazy(() =>
  import('@/pages/flows/FlowsPage').then((m) => ({ default: m.FlowsPage }))
);
const FlowBuilderPage = lazy(() =>
  import('@/pages/flows/FlowBuilderPage').then((m) => ({ default: m.FlowBuilderPage }))
);
const OnboardingWizard = lazy(() =>
  import('@/pages/onboarding/OnboardingWizard').then((m) => ({ default: m.OnboardingWizard }))
);
const KnowledgeBasePage = lazy(() =>
  import('@/pages/knowledge-base/KnowledgeBasePage').then((m) => ({ default: m.KnowledgeBasePage }))
);
const ConnectorsPage = lazy(() =>
  import('@/pages/connectors/ConnectorsPage').then((m) => ({ default: m.ConnectorsPage }))
);
const SettingsPage = lazy(() =>
  import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const ThemeEditorPage = lazy(() =>
  import('@/pages/settings/ThemeEditorPage').then((m) => ({ default: m.ThemeEditorPage }))
);
const SantinhoEditorPage = lazy(() =>
  import('@/pages/settings/SantinhoEditorPage').then((m) => ({ default: m.SantinhoEditorPage }))
);
const PublicPageEditorPage = lazy(() =>
  import('@/pages/settings/public-page').then((m) => ({ default: m.PublicPageEditorPage }))
);
const RolesManagementPage = lazy(() =>
  import('@/pages/settings/RolesManagementPage').then((m) => ({ default: m.RolesManagementPage }))
);
const TeamManagementPage = lazy(() =>
  import('@/pages/settings/TeamManagementPage').then((m) => ({ default: m.TeamManagementPage }))
);
const SubscriptionPage = lazy(() =>
  import('@/pages/settings/SubscriptionPage').then((m) => ({ default: m.SubscriptionPage }))
);
const TagsSettingsPage = lazy(() =>
  import('@/pages/settings/TagsSettingsPage').then((m) => ({ default: m.TagsSettingsPage }))
);
const OrganizationCategoriesSettingsPage = lazy(() =>
  import('@/pages/settings/OrganizationCategoriesSettingsPage').then((m) => ({
    default: m.OrganizationCategoriesSettingsPage,
  }))
);
const TemplatesSettingsPage = lazy(() =>
  import('@/pages/settings/TemplatesSettingsPage').then((m) => ({
    default: m.TemplatesSettingsPage,
  }))
);
const ProfilePage = lazy(() =>
  import('@/pages/profile').then((m) => ({ default: m.ProfilePage }))
);

const AdminDashboardPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminDashboardPage }))
);
const PlansPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.PlansPage }))
);
const TenantsPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.TenantsPage }))
);
const UsersPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.UsersPage }))
);
const PermissionsPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.PermissionsPage }))
);
const AdminSettingsPage = lazy(() =>
  import('@/pages/admin').then((m) => ({ default: m.AdminSettingsPage }))
);
const TenantDetailPage = lazy(() =>
  import('@/pages/admin/TenantDetailPage').then((m) => ({ default: m.TenantDetailPage }))
);
const BillingOverviewPage = lazy(() =>
  import('@/pages/admin/BillingOverviewPage').then((m) => ({ default: m.BillingOverviewPage }))
);
const WhatsAppMonitoringPage = lazy(() =>
  import('@/pages/admin/WhatsAppMonitoringPage').then((m) => ({
    default: m.WhatsAppMonitoringPage,
  }))
);
const CampaignsMonitoringPage = lazy(() =>
  import('@/pages/admin/CampaignsMonitoringPage').then((m) => ({
    default: m.CampaignsMonitoringPage,
  }))
);
const AuditLogPage = lazy(() =>
  import('@/pages/admin/AuditLogPage').then((m) => ({ default: m.AuditLogPage }))
);
const AwsCostsPage = lazy(() =>
  import('@/pages/admin/AwsCostsPage').then((m) => ({ default: m.AwsCostsPage }))
);

// Election BI module — feature-gated (add-on election-bi)
const ElectionsPage = lazy(() =>
  import('@/pages/elections/ElectionsPage').then((m) => ({ default: m.ElectionsPage }))
);
const ElectionStatePage = lazy(() =>
  import('@/pages/elections/ElectionStatePage').then((m) => ({ default: m.ElectionStatePage }))
);
const CandidatesListPage = lazy(() =>
  import('@/pages/elections/CandidatesListPage').then((m) => ({ default: m.CandidatesListPage }))
);
const CandidateProfilePage = lazy(() =>
  import('@/pages/elections/CandidateProfilePage').then((m) => ({ default: m.CandidateProfilePage }))
);
const PartiesRankingPage = lazy(() =>
  import('@/pages/elections/PartiesRankingPage').then((m) => ({ default: m.PartiesRankingPage }))
);
const AdminElectionsPage = lazy(() =>
  import('@/pages/admin/elections/AdminElectionsPage').then((m) => ({ default: m.AdminElectionsPage }))
);
const UploadWizardPage = lazy(() =>
  import('@/pages/admin/elections/UploadWizardPage').then((m) => ({ default: m.UploadWizardPage }))
);
const DatasetDetailPage = lazy(() =>
  import('@/pages/admin/elections/DatasetDetailPage').then((m) => ({ default: m.DatasetDetailPage }))
);
const ElectionUpgradePrompt = lazy(() =>
  import('@/components/elections/ElectionUpgradePrompt').then((m) => ({ default: m.ElectionUpgradePrompt }))
);
const RequireFeature = lazy(() =>
  import('@/components/guards/RequireFeature').then((m) => ({ default: m.RequireFeature }))
);
const ScenariosListPage = lazy(() =>
  import('@/pages/elections/scenarios/ScenariosListPage').then((m) => ({ default: m.ScenariosListPage }))
);
const ScenarioWizardPage = lazy(() =>
  import('@/pages/elections/scenarios/ScenarioWizardPage').then((m) => ({ default: m.ScenarioWizardPage }))
);
const ScenarioEditorPage = lazy(() =>
  import('@/pages/elections/scenarios/ScenarioEditorPage').then((m) => ({ default: m.ScenarioEditorPage }))
);

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return !isAuthenticated ? <>{children}</> : <Navigate to="/app" replace />;
}

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="h-10 w-10 rounded-full border-2 border-ink-200 border-t-primary-600 animate-spin" />
    </div>
  );
}

// Google Analytics: dispara um page_view a cada mudança de rota, mas apenas em
// páginas públicas (landing/santinho). Em rotas `/app` o tracking é suprimido —
// ver utils/analytics.ts.
function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, location.search);
  }, [location.pathname, location.search]);
  return null;
}

export default function App() {
  return (
    <TenantProvider>
      <ThemeProvider>
        <AnalyticsTracker />
        <WebSocketManager />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public routes (landing, signup, santinho) */}
            <Route element={<PublicLayout />}>
              <Route index element={<PublicHomePage />} />
              <Route path="/lp-lancamento" element={<LandingLancamento />} />
              <Route path="/lp-deputados-2026" element={<LandingDeputados2026 />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/santinho" element={<SantinhoPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
            </Route>

            {/* Demo page - standalone layout */}
            <Route path="/demo" element={<DemoPage />} />

            {/* Auth routes */}
            <Route
              path="/login"
              element={
                <PublicOnlyRoute>
                  <AuthLayout>
                    <LoginPage />
                  </AuthLayout>
                </PublicOnlyRoute>
              }
            />
            <Route path="/auth/callback" element={<CallbackPage />} />
            <Route
              path="/invite/:token"
              element={
                <AuthLayout>
                  <InvitePage />
                </AuthLayout>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <AuthLayout>
                  <ForgotPasswordPage />
                </AuthLayout>
              }
            />
            <Route
              path="/reset-password"
              element={
                <AuthLayout>
                  <ResetPasswordPage />
                </AuthLayout>
              }
            />

            {/* Onboarding - full-screen, outside DashboardLayout */}
            <Route
              path="/app/onboarding"
              element={
                <PrivateRoute>
                  <SubscriptionGuard>
                    <OnboardingWizard />
                  </SubscriptionGuard>
                </PrivateRoute>
              }
            />

            {/* Flow Builder - full-screen, outside DashboardLayout */}
            <Route
              path="/app/flows/new"
              element={
                <PrivateRoute>
                  <SubscriptionGuard>
                    <FlowBuilderPage />
                  </SubscriptionGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/app/flows/:id"
              element={
                <PrivateRoute>
                  <SubscriptionGuard>
                    <FlowBuilderPage />
                  </SubscriptionGuard>
                </PrivateRoute>
              }
            />

            {/* Private app routes */}
            <Route
              path="/app"
              element={
                <PrivateRoute>
                  <SubscriptionGuard>
                    <DashboardLayout />
                  </SubscriptionGuard>
                </PrivateRoute>
              }
            >
              <Route index element={<SmartRedirect />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="contacts/map" element={<ContactsMapPage />} />
              <Route
                path="contacts/new"
                element={
                  <PermissionGuard resource="contacts" action="create" redirectTo="/app/contacts">
                    <ContactFormPage />
                  </PermissionGuard>
                }
              />
              <Route
                path="contacts/:id/edit"
                element={
                  <PermissionGuard resource="contacts" action="edit" redirectTo="/app/contacts">
                    <ContactFormPage />
                  </PermissionGuard>
                }
              />
              <Route path="organizations" element={<OrganizationsPage />} />
              <Route path="organizations/map" element={<OrganizationsMapPage />} />
              <Route
                path="organizations/new"
                element={
                  <PermissionGuard
                    resource="organizations"
                    action="create"
                    redirectTo="/app/organizations"
                  >
                    <OrganizationFormPage />
                  </PermissionGuard>
                }
              />
              <Route
                path="organizations/:id/edit"
                element={
                  <PermissionGuard
                    resource="organizations"
                    action="edit"
                    redirectTo="/app/organizations"
                  >
                    <OrganizationFormPage />
                  </PermissionGuard>
                }
              />
              <Route path="demands" element={<DemandsPage />} />
              <Route path="demands/map" element={<DemandsMapPage />} />
              <Route path="supporters" element={<SupportersPage />} />
              <Route
                path="supporters/new"
                element={
                  <PermissionGuard resource="supporters" action="create" redirectTo="/app/supporters">
                    <SupporterFormPage />
                  </PermissionGuard>
                }
              />
              <Route
                path="supporters/:id"
                element={
                  <PermissionGuard resource="supporters" action="edit" redirectTo="/app/supporters">
                    <SupporterFormPage />
                  </PermissionGuard>
                }
              />
              <Route path="me/militancia" element={<MeMilitanciaPage />} />
              <Route
                path="supporters/dashboard"
                element={
                  <PermissionGuard
                    resource="supporters"
                    action="view"
                    redirectTo="/app/supporters"
                  >
                    <SupportersDashboardPage />
                  </PermissionGuard>
                }
              />
              <Route
                path="supporters/teams"
                element={
                  <PermissionGuard resource="teams" action="view" redirectTo="/app/supporters">
                    <TeamsPage />
                  </PermissionGuard>
                }
              />
              <Route
                path="supporters/tasks"
                element={
                  <PermissionGuard
                    resource="supporters"
                    action="view"
                    redirectTo="/app/supporters"
                  >
                    <TasksPage />
                  </PermissionGuard>
                }
              />
              <Route
                path="demands/new"
                element={
                  <PermissionGuard resource="demands" action="create" redirectTo="/app/demands">
                    <DemandFormPage />
                  </PermissionGuard>
                }
              />
              <Route
                path="demands/:id/edit"
                element={
                  <PermissionGuard resource="demands" action="edit" redirectTo="/app/demands">
                    <DemandFormPage />
                  </PermissionGuard>
                }
              />
              <Route path="chat" element={<ChatPage />} />
              <Route path="campaigns" element={<CampaignsPage />} />
              <Route path="campaigns/birthday" element={<BirthdayCampaignsPage />} />
              <Route
                path="campaigns/new"
                element={
                  <PermissionGuard resource="campaigns" action="create" redirectTo="/app/campaigns">
                    <CampaignFormPage />
                  </PermissionGuard>
                }
              />
              <Route path="campaigns/:id" element={<CampaignDetailPage />} />
              <Route
                path="campaigns/:id/edit"
                element={
                  <PermissionGuard resource="campaigns" action="edit" redirectTo="/app/campaigns">
                    <CampaignFormPage />
                  </PermissionGuard>
                }
              />
              <Route path="flows" element={<FlowsPage />} />
              <Route path="knowledge-bases" element={<KnowledgeBasePage />} />
              <Route path="connectors" element={<ConnectorsPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/public-page" element={<PublicPageEditorPage />} />
              {/* Legacy redirects — preserva links antigos */}
              <Route
                path="settings/theme"
                element={<Navigate to="/app/settings/public-page?tab=design" replace />}
              />
              <Route
                path="settings/santinho"
                element={<Navigate to="/app/settings/public-page?tab=content" replace />}
              />
              {/* Rotas antigas (compat, escondidas do menu) */}
              <Route path="settings/theme-legacy" element={<ThemeEditorPage />} />
              <Route path="settings/santinho-legacy" element={<SantinhoEditorPage />} />
              <Route path="settings/roles" element={<RolesManagementPage />} />
              <Route path="settings/team" element={<TeamManagementPage />} />
              <Route path="settings/subscription" element={<SubscriptionPage />} />
              <Route path="settings/tags" element={<TagsSettingsPage />} />
              <Route path="settings/organization-categories" element={<OrganizationCategoriesSettingsPage />} />
              <Route path="settings/templates" element={<TemplatesSettingsPage />} />

              {/* Election BI — feature-gated: election-bi add-on + electionScenarios.view */}
              <Route
                path="elections"
                element={
                  <RequireFeature feature="election-bi" fallback={<ElectionUpgradePrompt />}>
                    <PermissionGuard resource="electionScenarios" action="view" showAccessDenied>
                      <ElectionsPage />
                    </PermissionGuard>
                  </RequireFeature>
                }
              />
              <Route
                path="elections/:electionKey/:uf"
                element={
                  <RequireFeature feature="election-bi" fallback={<ElectionUpgradePrompt />}>
                    <PermissionGuard resource="electionScenarios" action="view" showAccessDenied>
                      <ElectionStatePage />
                    </PermissionGuard>
                  </RequireFeature>
                }
              />
              <Route
                path="elections/:electionKey/:uf/municipios/:ibgeId"
                element={
                  <RequireFeature feature="election-bi" fallback={<ElectionUpgradePrompt />}>
                    <PermissionGuard resource="electionScenarios" action="view" showAccessDenied>
                      <ElectionStatePage />
                    </PermissionGuard>
                  </RequireFeature>
                }
              />
              <Route
                path="elections/:electionKey/:uf/candidatos"
                element={
                  <RequireFeature feature="election-bi" fallback={<ElectionUpgradePrompt />}>
                    <PermissionGuard resource="electionScenarios" action="view" showAccessDenied>
                      <CandidatesListPage />
                    </PermissionGuard>
                  </RequireFeature>
                }
              />
              <Route
                path="elections/:electionKey/:uf/candidatos/:id"
                element={
                  <RequireFeature feature="election-bi" fallback={<ElectionUpgradePrompt />}>
                    <PermissionGuard resource="electionScenarios" action="view" showAccessDenied>
                      <CandidateProfilePage />
                    </PermissionGuard>
                  </RequireFeature>
                }
              />
              <Route
                path="elections/:electionKey/:uf/partidos"
                element={
                  <RequireFeature feature="election-bi" fallback={<ElectionUpgradePrompt />}>
                    <PermissionGuard resource="electionScenarios" action="view" showAccessDenied>
                      <PartiesRankingPage />
                    </PermissionGuard>
                  </RequireFeature>
                }
              />

              {/* Cenários Eleitorais — feature-gated: election-bi add-on + electionScenarios.view */}
              <Route
                path="elections/scenarios"
                element={
                  <RequireFeature feature="election-bi" fallback={<ElectionUpgradePrompt />}>
                    <PermissionGuard resource="electionScenarios" action="view" showAccessDenied>
                      <ScenariosListPage />
                    </PermissionGuard>
                  </RequireFeature>
                }
              />
              <Route
                path="elections/scenarios/new"
                element={
                  <RequireFeature feature="election-bi" fallback={<ElectionUpgradePrompt />}>
                    <PermissionGuard resource="electionScenarios" action="view" showAccessDenied>
                      <ScenarioWizardPage />
                    </PermissionGuard>
                  </RequireFeature>
                }
              />
              <Route
                path="elections/scenarios/:scenarioId"
                element={
                  <RequireFeature feature="election-bi" fallback={<ElectionUpgradePrompt />}>
                    <PermissionGuard resource="electionScenarios" action="view" showAccessDenied>
                      <ScenarioEditorPage />
                    </PermissionGuard>
                  </RequireFeature>
                }
              />

              {/* Admin routes - protected by SuperAdminGuard */}
              <Route
                path="admin"
                element={
                  <SuperAdminGuard>
                    <AdminDashboardPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/plans"
                element={
                  <SuperAdminGuard>
                    <PlansPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/tenants"
                element={
                  <SuperAdminGuard>
                    <TenantsPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/tenants/:id"
                element={
                  <SuperAdminGuard>
                    <TenantDetailPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/billing"
                element={
                  <SuperAdminGuard>
                    <BillingOverviewPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/whatsapp"
                element={
                  <SuperAdminGuard>
                    <WhatsAppMonitoringPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/campaigns"
                element={
                  <SuperAdminGuard>
                    <CampaignsMonitoringPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/aws-costs"
                element={
                  <SuperAdminGuard>
                    <AwsCostsPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/audit-log"
                element={
                  <SuperAdminGuard>
                    <AuditLogPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/users"
                element={
                  <SuperAdminGuard>
                    <UsersPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/permissions"
                element={
                  <SuperAdminGuard>
                    <PermissionsPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/settings"
                element={
                  <SuperAdminGuard>
                    <AdminSettingsPage />
                  </SuperAdminGuard>
                }
              />

              {/* Admin elections — dataset management (SuperAdminGuard) */}
              <Route
                path="admin/elections"
                element={
                  <SuperAdminGuard>
                    <AdminElectionsPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/elections/upload"
                element={
                  <SuperAdminGuard>
                    <UploadWizardPage />
                  </SuperAdminGuard>
                }
              />
              <Route
                path="admin/elections/datasets/:id"
                element={
                  <SuperAdminGuard>
                    <DatasetDetailPage />
                  </SuperAdminGuard>
                }
              />
            </Route>

            {/* Legacy redirect: / to /app for authenticated users */}
            <Route path="/dashboard" element={<Navigate to="/app" replace />} />

            {/* 404 - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ThemeProvider>
    </TenantProvider>
  );
}
